<template>
  <div class="main">
    <!-- Navigation bar -->
    <editor-navbar
      :current-channel-id="currentChannel"
      :channels="channels"
      :active-socials="activeSocials"
      :is-new-notif="isNewNotif"
      :collapsing-limit="mobileWidthLimit"
      class="editorNavbar"
      @channelSelected="switchChannel"
      @addAccount="addSocialAccount"
      @removeAccount="handleSelectSocial($event.social, $event.id)"
      @openShortcuts="openShortcutsInfo"
      @openNotifs="isNotifsOpened = true"
      @openCalendar="openModalCalenar"
    />
    <!-- Main app container -->
    <div :id="$options.editorId" class="editorContainer scroll-invisible">
      <!-- Channel player -->
      <div ref="editorPlayerContainer" class="editorPlayer">
        <ndvr-video-player
          ref="ndvrPlayer"
          :ndvr-stream="ndvrStream"
          :poster="poster"
          :time="playerTime"
          :to-play="isPlaying"
          :has-video="hasThumbnails"
          :crop-slider-mode="cropSliderMode"
          @valueCheckboxThumbnails="valueCheckboxThumbnails = $event"
          @valueRangeInfo="valueCheckboxRange = $event"
          @updateTime="updateTime"
          @play="startPlayer"
          @pause="stopPlayer"
          @moveLeftCrop="moveSliderLeftBound"
          @moveRightCrop="moveSliderRightBound"
          @moveLeftCropToCursor="moveLeftSliderToPlayer"
          @moveRightCropToCursor="moveRightSliderToPlayer"
        />
      </div>

      <!-- Videos list -->
      <div class="editorMedia">
        <media-editor
          :channels="channels"
          :media-items="mediaItems"
          :open-modal-express-sharing="openModalExpressSharing"
          @stopUpdateVideos="handleStopUpdateVideos"
          @continueUpdateVideos="handleContinueUpdateVideos"
          @stopPlayer="stopPlayer"
          @gifRemoved="handleRemoveGIF"
          @cloneItemVideo="cloneVideoItem"
          @sharingRemoved="handleRemoveSharing($event)"
        />
      </div>
      <!-- Channel timeline -->
      <div class="editorTimeline">
        <timeline-editor
          class="editorTimeline__timeline"
          :has-thumbnails="hasThumbnails"
          :breaks="breaks"
          :scale-mode="timelineScaleMode"
          :scale-length="timelineLabelsCount"
          :player-time="playerTime"
          :show-thumbnails="valueCheckboxThumbnails"
          :value-checkbox-range="valueCheckboxRange"
          :trim-slider="[leftCropBound, rightCropBound]"
          @timeCursorMove="handleTimeCursorMove"
          @sliderMarkInPlayerMove="handleSliderMove"
          @sliderMarkOutPlayerMove="handleSliderMove"
          @cropSliderModeChanged="handleCropSliderModeChanged"
          @movePlayer="updateTime"
          @trimSliderMoved="handleTrimSliderMove"
          @zoom="handleZoom"
        />
      </div>
      <!-- Channel clipping controls -->
      <div class="editorCropControls">
        <crop-controls :media-items="mediaItems" @sendCrop="sendCropRequest" />
      </div>
    </div>
    <!-- Notifications sidebar -->
    <notifications
      class="editorNotifs"
      :timezone="localTimezone"
      :is-opened="isNotifsOpened"
      @opened="isNotifsOpened = true"
      @closed="isNotifsOpened = false"
      @notificationReceived="isNewNotif = true"
      @notificationsRead="isNewNotif = false"
    />
    <!-- Modal forms -->
    <modals-manager
      :channel-id="channelId"
      @selectModal="selectModals = $event"
    />
  </div>
</template>

<script>
import EditorNavbar from "@/components/navbar/EditorNavbar.vue";
import NdvrVideoPlayer from "@/components/media/video/NdvrVideoPlayer.vue";
import MediaEditor from "@/components/media/MediaEditor.vue";
import TimelineEditor from "@/components/timeline/TimelineEditor.vue";
import CropControls from "@/components/media/controls/CropControls.vue";
import Notifications from "@/components/notifications/Notifications.vue";
import { mapActions, mapGetters, mapState, mapMutations } from "vuex";

/** modals manager */
import ModalsManager from "@/components/dialog/ModalsManager.vue";

import { getUserData } from "@/api/auth.js";
import mediaApi from "@/api/media/media.js";
import { removeSharing } from "@/api/socials/sharings.js";
import { getUrlParamsByKey } from "@/utils/urls.js";
import {
  EDITOR_ID,
  SCALE_MODE,
  TIMELINE_UPDATE_INTERVAL,
  STATISTIC_UPDATE_INTERVAL,
} from "@/constants.js";
import { roundToMilliseconds } from "@/utils/time.js";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { setInterval } from "timers";
import { token } from "@/api/params.js";

dayjs.extend(utc);
dayjs.extend(timezone);

// Main editor application
export default {
  name: "Editor",
  components: {
    EditorNavbar,
    NdvrVideoPlayer,
    MediaEditor,
    TimelineEditor,
    CropControls,
    Notifications,
    ModalsManager,
  },
  reloadTimeout: 15000,
  editorId: EDITOR_ID,
  props: {
    /** initiation channel ID*/
    channelId: {
      type: String,
      required: true,
    },
    userInfo: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      videosList: [],
      gifsList: [],
      upload: [],
      audiosList: [],
      openModalExpressSharing: null,
      valueCheckboxThumbnails: true,
      valueCheckboxRange: false,
      /** loading background */
      loadingInstance: undefined,
      mobileWidthLimit: 992,
      isNotifsOpened: false,
      isNewNotif: false,
      windowWidth: document.body.clientWidth,
      cropSliderMode: "none",
      criticalError: {
        error: null,
        permanent: false,
      },
      selectModals: "",
    };
  },
  computed: {
    ...mapGetters(["hasThumbnails"]),
    ...mapGetters("timeline", ["visibleTimelineLength", "ndvrStreamLength"]),
    ...mapState({
      /** stream params */
      streamHost: (state) => state.services.host,
      streamApp: (state) => state.services.app,
      streamName: (state) => state.services.stream,
      streamToken: (state) => state.services.stream_token,
      /** player */
      playerTime: (state) => state.ndvrPlayer.displayTimer,
      isPlaying: (state) => state.ndvrPlayer.isPlaying,
      poster: (state) => state.services.channelPoster,
      /** timeline */
      timelineZoom: (state) => state.timeline.zoom,
      timelineVisibleLeft: (state) => state.timeline.visibleTimelineLeft,
      timelineVisibleRight: (state) => state.timeline.visibleTimelineRight,
      ndvrStreamStart: (state) => state.timeline.ndvrStreamStart,
      ndvrStreamEnd: (state) => state.timeline.ndvrStreamEnd,
      leftCropBound: (state) => state.timeline.sliderLeftBound,
      rightCropBound: (state) => state.timeline.sliderRightBound,
      breaks: (state) => state.timeline.breaks,
      /** API */
      app: (state) => state.services.app,
      stream: (state) => state.services.stream,
      storagePath: (state) => state.services.thumbs,
      currentChannel: (state) => state.userData.currentChannel,
      channels: (state) => state.userData.channels,
      defaultTimezone: (state) => state.userData.defaultTimezone,
      /** Socials */
      youtube: (state) => state.socials.youtube,
      facebook: (state) => state.socials.facebook,
      twitter: (state) => state.socials.twitter,
      sharings: (state) => state.socials.sharings,
      /** modals */
      modals: (state) => state.modals.clipper.MODALS,
    }),
    isStreamUrlReceived() {
      if (this.streamToken === undefined) {
        return this.streamHost && this.streamName;
      } else {
        return this.streamHost && this.streamName && this.streamToken;
      }
    },
    // @vuese
    // nDVR parameters endpoint
    ndvrStream() {
      if (!this.isStreamUrlReceived) {
        return null;
      }
      const link = `${this.$store.state.services.host}/ndvr/${this.$store.state.services.stream}/playlist.m3u8?token=${this.$store.state.services.stream_token}`;
      return link;
    },
    // @vuese
    // Current media items lists
    mediaItems() {
      if (this.hasThumbnails) {
        return {
          clips: this.videosList,
          gifs: this.gifsList,
          upload: true,
        };
      } else {
        return {
          audios: this.audiosList,
          upload: true,
        };
      }
    },
    // @vuese
    // Using socials list with their labels and connected accounts data
    socialsList() {
      return {
        youtube: this.youtube,
        facebook: this.facebook,
        twitter: this.twitter,
      };
    },
    // @vuese
    // List of socials that have connected accounts
    activeSocials() {
      const resultSocials = {};

      for (const socialName in this.socialsList) {
        const social = this.socialsList[socialName];
        if (social.accounts.length > 0) {
          resultSocials[socialName] = social;
        }
      }

      return resultSocials;
    },
    // @vuese
    // User timezone name
    localTimezone: {
      cache: false,
      get() {
        return dayjs.tz.guess();
      },
    },
    // @vuese
    // How near user see the timeline
    timelineScaleMode() {
      if (this.timelineZoom > 0.8) {
        return SCALE_MODE.FARAWAY;
      } else if (this.timelineZoom > 0.6) {
        return SCALE_MODE.AWAY;
      } else if (this.timelineZoom > 0.4) {
        return SCALE_MODE.NORMAL;
      } else if (this.timelineZoom > 0.2) {
        return SCALE_MODE.NEAR;
      } else {
        return SCALE_MODE.CLOSE;
      }
    },
    // @vuese
    // Count of time marks on top of timeline panel
    timelineLabelsCount() {
      if (this.windowWidth > 1200) {
        return 7;
      } else if (this.windowWidth > 640) {
        return 6;
      } else if (this.windowWidth > 400) {
        return 5;
      } else {
        return 4;
      }
    },
  },
  watch: {
    "criticalError.error"() {
      const { error, permanent } = this.criticalError;
      if (error) {
        this.showCriticalError(error);

        if (!permanent) {
          setTimeout(() => {
            window.location.reload();
          }, this.$options.reloadTimeout);
        }
      } else {
        this.hideCriticalError();
      }
    },
    // @vuese
    // Compare the old ID with the new one
    currentChannel(newChannel, oldChannel) {
      if (oldChannel == null || newChannel !== this.channelId) {
        this.$router.push(`/clipper/channels/${newChannel}?token=${token}`);
      }
    },
    channelId() {
      location.reload();
    },
  },
  created() {
    /** check watch link to route user to video */
    // eslint-disable-next-line no-undef
    const storagePrefix = process.env.VUE_APP_STORAGE_PREFIX;

    if (storagePrefix == undefined) {
      this.criticalError = {
        error: "Setting error: storage prefix is not set",
        permanent: true,
      };
      return;
    }

    const watchPath = getUrlParamsByKey("watch")[0];
    if (watchPath) {
      const decodedPath = decodeURIComponent(watchPath);
      const watchPattern = /^[a-z0-9./_]+$/i;
      if (watchPattern.test(decodedPath)) {
        document.location.href = `${storagePrefix}/${watchPath}`;
      } else {
        this.criticalError = {
          error: "Request error: watch link is not valid",
          permanent: true,
        };
      }
    }
  },
  async mounted() {
    if (this.criticalError.error) {
      return;
    }
    if (this.channelId != this.currentChannel) {
      // this.showError('Stream with this id does not exist, you are redirected to another stream')
      if (this.channelId == "null") {
        // this.showError('Stream with this id does not exist, you are redirected to another stream')
        this.$router.push(
          `/clipper/channels/${this.userInfo.channels[0].id}?token=${token}`
        );
      } else {
        this.$router.push(
          `/clipper/channels/${this.currentChannel}?token=${token}`
        );
      }

      // this.$router.push(
      //   `/clipper/channels/${this.userInfo.channels[0].id}?token=${token}`
      // );
    }

    try {
      /** media height fixing */
      window.addEventListener(
        "resize",
        () => (this.windowWidth = document.body.clientWidth)
      );

      /** show download spinner */
      // this.showLoading("Loading");

      const userInfo = this.userInfo;

      if (userInfo.channels.length != 0) {
        //если нет чанела поставить нулевой
        const defaultChannel = userInfo.channels[0].id;
        /** if token valid and we got list of channels */
        this.initializeUserData({
          userData: userInfo,
          channelId:
            this.channelId == undefined ? defaultChannel : this.channelId,
        });
        if (this.defaultTimezone) {
          this.initTimezone({
            defaultTimezone: this.defaultTimezone,
            channelId: this.channelId,
          });
        } else {
          this.initTimezone("UTC");
        }

        /** getting initial ndvr data and initialize timeline */
        this.initTimeline();
        try {
          await this.updateBreaks();
        } catch (error) {
          console.error(`Stream breaks gettng failed: ${error}`);
        }
        setInterval(this.synchronizeTime, TIMELINE_UPDATE_INTERVAL);
        setInterval(this.updateBreaks, STATISTIC_UPDATE_INTERVAL);

        // this.hideLoading();

        this.subscribeToMedia();

        /** init socials */
      } else {
        /** if token valid and we got empty list of channels */
        throw Error("No Streams allowed");
      }
    } catch (e) {
      /** if token invalid */
      // this.hideLoading();
      this.criticalError = {
        error: `Clipper Error : ${e}, please try again later`,
        permanent: false,
      };
    }
  },
  methods: {
    ...mapActions("ndvrPlayer", {
      updateTimer: "updateTimer",
      startPlayer: "play",
      stopPlayer: "pause",
    }),
    ...mapActions("thumbnails", ["loadThumbnails"]),
    ...mapActions("timeline", [
      "initTimeline",
      "synchronizeTime",
      "updateBreaks",
      "updateCursor",
      "moveSliderLeftBound",
      "moveSliderRightBound",
      "moveSliderBoth",
      "zoomIn",
      "zoomOut",
    ]),
    ...mapActions("socials", [
      "addAccount",
      "deleteAccount",
      "setSharing",
      "excludeSharing",
    ]),
    ...mapActions("timezone", ["initTimezone"]),
    ...mapActions("modals", ["openClipperModal"]),
    ...mapActions({
      initializeUserData: "initializeUserData",
      setChannel: "setChannel",
    }),
    subscribeToMedia() {
      if (this.hasThumbnails) {
        this.subscribeToVideos();
        this.subscribeToGIFs();
      } else {
        this.subscribeToAudios();
      }
    },
    cloneVideoItem(arrVideoInfo) {
      console.log("clone");
      // this.videosList.push(arrVideoInfo);
    },
    unsubscribeFromMedia() {
      if (this.hasThumbnails) {
        mediaApi.unsubscribeFromVideoUpdates();
        mediaApi.unsubscribeFromGIFUpdates();
      } else {
        mediaApi.unsubscribeFromAudioUpdates();
      }
    },
    // @vuese
    // Enable videos updating
    subscribeToVideos() {
      mediaApi.subscribeToVideoUpdates(
        this.app,
        this.stream,
        (videos) => {
          this.videosList = videos.slice();
          // Update sharings data when video updated
          this.videosList.forEach((v) => {
            v.sharings.forEach((s) =>
              this.setSharing({
                id: s.id,
                videoId: v.id,
                social: s.target,
                status: s.status,
                link: s.link ? s.link : null,
                percentage: s.percentage ? s.percentage : null,
                socialVideoId: s.socialVideoId,
                accountId: s.userSocialAccountId,
                publishTime: s.startPublishingTime,
              })
            );
          });
        },
        (e) => {
          console.error(e);
          this.showError("Fetch Videos List Error", e);
        }
      );
    },
    // @vuese
    // Restart video list updating
    handleContinueUpdateVideos() {
      this.subscribeToVideos();
    },
    // @vuese
    // Stop video list updating
    handleStopUpdateVideos() {
      mediaApi.unsubscribeFromVideoUpdates();
    },
    // @vuese
    // Enable GIFs updating
    subscribeToGIFs() {
      mediaApi.subscribeToGIFUpdates(
        this.app,
        this.stream,
        (gifs) => {
          this.gifsList = gifs;
        },
        (e) => {
          console.error(e);
          this.showError("Fetch GIFs List Error", e);
        }
      );
    },
    // @vuese
    // Enable audios updating
    subscribeToAudios() {
      mediaApi.subscribeToAudioUpdates(
        this.app,
        this.stream,
        (audios) => {
          this.audiosList = audios;
          this.audiosList.forEach((v) => {
            v.sharings.forEach((s) =>
              this.setSharing({
                id: s.id,
                videoId: v.id,
                social: s.target,
                status: s.status,
                link: s.link ? s.link : null,
                percentage: s.percentage ? s.percentage : null,
                socialVideoId: s.socialVideoId,
                accountId: s.userSocialAccountId,
                publishTime: s.startPublishingTime,
                socialAudioId: s.socialAudioId,
              })
            );
          });
        }
        // error: e => {
        //   console.error(e);
        //   this.showError("Fetch Ausdios List Error", e);
        // }
      );
    },
    // @vuese
    // Switch current channel
    // @arg value - new channel ID
    async switchChannel(value) {
      if (value == this.currentChannel) {
        return;
      }

      this.unsubscribeFromMedia();
      /** this.showLoading("Loading");*/
      this.setChannel(value);
      /** this.hideLoading(); */
      this.initTimeline();
      this.updateBreaks();

      if (this.hasThumbnails) {
        this.loadThumbnails();
      }
      this.subscribeToMedia();
    },
    // @vuese
    // Move player to time position
    // @arg position - absolute time to set
    handleSliderMove(time) {
      this.updateTime(time);
    },
    // @vuese
    // Move player to relative time position
    // @arg position - relative position in visible area (0 - left, 1 - right)
    handleTimeCursorMove(position) {
      const hidden = this.timelineVisibleLeft;
      // Count visible time
      const visible =
        position * (this.timelineVisibleRight - this.timelineVisibleLeft);
      const time = visible + hidden;

      this.updateTime(roundToMilliseconds(time));
    },
    // @vuese
    // Update player cursor position
    // @arg time - absolute time UNIX-timestamp
    updateTime(time) {
      const boundedTime = Math.min(
        Math.max(time, this.ndvrStreamStart),
        this.ndvrStreamEnd
      );
      const roundedTime = roundToMilliseconds(boundedTime);
      this.updateTimer(roundedTime);
      this.updateCursor(roundedTime);
    },
    // @vuese
    // Send request to API server to generate new clip from crop borders end trim & Share
    async sendCropRequest(express) {
      try {
        for (const { start, end } of this.breaks) {
          /** check intersection with break */
          if (this.rightCropBound >= start && this.leftCropBound <= end) {
            this.showError(
              "Trim Range Error",
              "Failed stream fragment is between left and right trim handles"
            );
            return;
          }
        }

        const cropFunction = this.hasThumbnails
          ? mediaApi.cropVideo
          : mediaApi.cropAudio;

        const resp = await cropFunction(
          this.app,
          this.stream,
          roundToMilliseconds(this.leftCropBound),
          roundToMilliseconds(this.rightCropBound),
          `${this.stream}-${dayjs().format("YY.MM.DD HH:mm:ss")}`,
          true
        );
        if (express) {
          this.openModalExpressSharing = resp;
        }
        this.showSuccess("Conversion started", `New conversion added to queue`);
      } catch (e) {
        this.showError(
          "Conversion Start Error",
          `Conversion Start Failed: ${e}`
        );
      }
    },
    sendCropEndTrimRequest() {
      this.openModalExpressSharing = true;
    },
    // @vuese
    // Show disconnect account confiration
    // @arg social - Social name (facebook/youtube/twitter) <br>
    // @arg id - universal social account identifier
    handleSelectSocial(social, id) {
      this.$confirm(
        `Do you want to remove this account? It will remove all connecting sharing info.`,
        "Disconnect",
        {
          confirmButtonText: "Disconnect",
          cancelButtonText: "Cancel",
        }
      ).then(() => this.handleAccountDisconnecting(social, id));
    },
    // @vuese
    // Try to disconnect account
    // @arg social - account's social <br>
    // @arg id - account's ID
    async handleAccountDisconnecting(social, id) {
      try {
        await this.deleteAccount({ social, id });
        console.log(
          `${social} account (id:${id}) was disconnected succesfully`
        );
        this.showSuccess("Success", "Account has disabled");
        this.sharings
          .filter((s) => s.accountId == id)
          .forEach((s) => {
            this.handleRemoveSharing({ videoId: s.videoId, sharingId: s.id });
          });
      } catch (e) {
        console.error(e);
        this.showError("Disconnecting Error", e);
      }
    },
    // @vuese
    // Remove sharing data
    // @arg videoId - shared video ID <br>
    // @arg sharingId - sharing data ID
    async handleRemoveSharing({ videoId, sharingId }) {
      try {
        await removeSharing(videoId, sharingId);
        this.excludeSharing(sharingId);
      } catch (e) {
        this.showError("Sharing Record Remove Failed", e);
      }
    },
    // @vuese
    // Connect new socials account
    // @arg social (youtube/facebook/twitter)
    async addSocialAccount(social) {
      try {
        await this.addAccount(social);
        this.showSuccess("Success", "Account has connected");
      } catch (e) {
        if (e.error !== "popup_closed_by_user") {
          this.showError("Socials Initialize Error", e);
          console.error(e);
        }
      }
    },
    // @vuese
    // Open shortcuts helper form
    openShortcutsInfo() {
      this.openClipperModal({
        modal: this.modals.SHORTCUTS,
        data: null,
      });
    },
    openModalCalenar() {
      this.openClipperModal({
        modal: this.modals.CALENDAR,
        data: null,
      });
    },
    // @vuese
    // Remove GIF from local data (not from server)
    // @arg id - GIF ID
    handleRemoveGIF(id) {
      const index = this.gifsList.findIndex((element) => {
        return id == element.id;
      });
      this.gifsList.splice(index, 1);
    },
    // @vuese
    // Zoom timeline
    handleZoom({ delta, pointer }) {
      if (delta > 0) {
        this.zoomOut(pointer);
      } else {
        this.zoomIn(pointer);
      }
    },
    // @vuese
    // Show error notification
    // @arg title - main subject <br>
    // @arg message - detalaized description
    showError(title, message) {
      this.$notify({
        group: "foo",
        position: "bottom right",
        type: "error",
        title: title,
        text: message,
      });
    },
    // @vuese
    // Show success notification
    // @arg title - main subject <br>
    // @arg message - detalaized description
    showSuccess(title, message) {
      this.$notify({
        group: "foo",
        position: "bottom right",
        type: "success",
        title: title,
        text: message,
      });
    },
    // @vuese
    // Show overlay with warning icon and custom message
    // @arg message - message to show
    showCriticalError(message) {
      this.errorInstance = this.$loading({
        lock: true,
        text: message,
        spinner: "el-icon-warning-outline",
        background: "rgba(0, 0, 0, 1)",
      });
    },
    // @vuese
    // Hide fullscreen error overlay
    hideCriticalError() {
      this.errorInstance.close();
    },
    // @vuese
    // Show fullscreen loading spinner
    // @arg message - loading text message
    showLoading(message) {
      this.loadingInstance = this.$loading({
        lock: true,
        text: message,
        spinner: "el-icon-loading",
        background: "rgba(0, 0, 0, 1)",
      });
    },
    // @vuese
    // Hide fullscreen loading spinner
    hideLoading() {
      this.loadingInstance.close();
    },
    // @vuese
    // getting new crop slider mode from emited event and assign it to crop Slider Mode
    // from data in order to pass it to NdvrVideoPlayer
    // @arg newCropSliderMode - crop slider mode after changing timeline zoom
    handleCropSliderModeChanged(newCropSliderMode) {
      this.cropSliderMode = newCropSliderMode;
    },
    handleTrimSliderMove([left, right]) {
      const leftDelta = roundToMilliseconds(left - this.leftCropBound);
      const rightDelta = roundToMilliseconds(right - this.rightCropBound);

      this.moveSliderLeftBound(leftDelta);
      this.moveSliderRightBound(rightDelta);
    },
    moveLeftSliderToPlayer() {
      const changeValue = roundToMilliseconds(
        this.playerTime - this.leftCropBound
      );
      if (this.playerTime < this.rightCropBound) {
        this.moveSliderLeftBound(changeValue);
      } else {
        /** if player cursor is from right of right crop then right bound then move slider at all */
        this.moveSliderBoth(changeValue);
      }
    },
    moveRightSliderToPlayer() {
      const changeValue = roundToMilliseconds(
        this.playerTime - this.rightCropBound
      );
      if (this.playerTime > this.leftCropBound) {
        this.moveSliderRightBound(changeValue);
      } else {
        /** if player cursor is from left of left crop then right bound then move slider at all */
        this.moveSliderBoth(changeValue);
      }
    },
  },
};
</script>

<style lang="scss">
body {
  overflow-x: hidden;
}

.main {
  height: 100vh;
  overflow: hidden;
}

.editorNavbar {
  width: 100%;
  min-height: 60px;
}

.logo {
  max-width: 125px;
  max-height: 250px;
  margin-top: 18px;
  margin-bottom: 5px;
  display: flex;
  padding-left: 20px;
  justify-self: start;
}

.editorContainer {
  height: calc(100% - 60px);

  display: grid;
  grid-template-columns: 60% 40%;
  grid-template-rows: 1fr 160px;
  max-width: 100vw;
  max-height: 100vh;
  justify-items: stretch;
  grid-template-areas:
    "player media"
    "timeline controls";

  background-color: #233d4e;
  overflow-x: hidden;
}

.preview .playerContainer {
  width: 67vw !important;
}

@keyframes dots {
  0%,
  20% {
    color: rgba(0, 0, 0, 0);
    text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0), 0.5em 0 0 rgba(0, 0, 0, 0);
  }
  40% {
    color: white;
    text-shadow: 0.25em 0 0 rgba(0, 0, 0, 0), 0.5em 0 0 rgba(0, 0, 0, 0);
  }
  60% {
    text-shadow: 0.25em 0 0 white, 0.5em 0 0 rgba(0, 0, 0, 0);
  }
  80%,
  100% {
    text-shadow: 0.25em 0 0 white, 0.5em 0 0 white;
  }
}

.editorPlayer {
  grid-area: player;
  display: block;
  background-color: transparent;
  color: white;
  z-index: var(--player-z);
}

.editorMedia {
  grid-area: media;
  border: solid 1px #423f3f;
  background-color: #233d4e;
  color: white;
  display: block;
  overflow: hidden;
  position: relative;
}

.editorCropControls {
  grid-area: controls;
  border: solid 1px var(--light-grey);
  background-color: #233d4e;
  color: white;
}

.editorTimeline {
  grid-area: timeline;

  width: 100%;
  box-sizing: border-box;

  display: flex;
  align-items: center;

  border: solid 1px var(--light-grey);
  background-color: #233d4e;
  color: white;
  padding: 5px;

  &__timeline {
    width: 100%;
    flex-grow: 1;
  }
}

.editorNotifs {
  z-index: var(--notifs-z);
}

@media only screen and (min-width: 400px) {
  .editorCropControls {
    max-height: 170px;
    margin-top: 0px;
  }
  .editorMedia {
    grid-area: media;
    border: solid 1px var(--light-grey);
    background-color: #233d4e;
    color: white;
    display: block;
    overflow: unset;
    // position: relative;
  }
}

/** media queries */
@media only screen and (max-width: 767px) {
  .editorContainer {
    grid-template-rows: 1fr 230px;
    max-width: 100vw;
  }
  .editorMedia {
    grid-area: media;
    border: solid 1px var(--light-grey);
    background-color: #233d4e;
    color: white;
    display: block;
    overflow: unset;
    // position: relative;
  }
}
@media only screen and (min-width: 768px) {
  .editorTimeline {
    max-height: 200px;
    height: 190px;
  }
  .editorCropControls {
    max-height: 170px;
    margin-top: 30px;
  }
  .editorMedia {
    grid-area: media;
    border: solid 1px var(--light-grey);
    background-color: #233d4e;
    color: white;
    display: block;
    overflow: unset;
    // position: relative;
  }
}
@media only screen and (max-width: 991px) {
  .editorNavbar {
    position: fixed;
    top: 0;
    z-index: var(--navbar-z);
  }
  .editorContainer {
    margin-top: 60px;
    grid-template-columns: 100vw;
    grid-template-areas:
      "player"
      "timeline"
      "controls"
      "media";
  }
  .editorPlayer {
    /** 16/9 + player controls panel height */
    // height: calc(56.25vw + 152px);
  }
}
@media only screen and (min-width: 992px) {
  .editorContainer {
    max-width: 100vw;
  }
  .editorTimeline {
    max-height: 200px;
    height: 190px;
  }
  .editorCropControls {
    max-height: 220px;
    height: 190px;
    margin-top: 0px;
  }
  .editorMedia {
    grid-area: media;
    border: solid 1px var(--light-grey);
    background-color: #233d4e;
    color: white;
    display: block;
    overflow: hidden;
    position: relative;
  }
}

@media only screen and (min-width: 1200px) {
  .editorContainer {
    max-width: 100vw;
  }
  .editorTimeline {
    max-height: 300px;
    height: 200px;
    margin: -40px 0;
  }
  .editorCropControls {
    max-height: 200px;
    height: 198px;
    margin-top: -40px;
  }
  .editorMedia {
    height: 95%;
    grid-area: media;
    border: solid 1px var(--light-grey);
    background-color: #233d4e;
    color: white;
    display: block;
    overflow: hidden;
    position: relative;
  }
}

@media only screen and (min-width: 1400px) {
  .editorContainer {
    max-width: 100vw;
  }
  .editorTimeline {
    max-height: 300px;
    height: 200px;
    margin: -40px 0;
  }
  .editorCropControls {
    max-height: 200px;
    height: 198px;
    margin-top: -40px;
  }
  .editorMedia {
    height: 95%;
    grid-area: media;
    border: solid 1px var(--light-grey);
    background-color: #233d4e;
    color: white;
    display: block;
    overflow: hidden;
    position: relative;
  }
}

@media only screen and (min-width: 1920px) {
  .editorContainer {
    max-width: 100vw;
  }
  .editorTimeline {
    max-height: 300px;
  }
  .editorCropControls {
    max-height: 300px;
    height: 198px;
    margin-top: -40px;
  }
  .editorMedia {
    height: 95%;
    grid-area: media;
    border: solid 1px var(--light-grey);
    background-color: #233d4e;
    color: white;
    display: block;
    overflow: hidden;
    position: relative;
  }
}
@media only screen and (min-width: 1921px) {
  .editorTimeline {
    max-height: 300px;
    height: 200px;
    margin-top: -10px;
  }
  .editorCropControls {
    max-height: 300px;
    height: 186px;
    margin-top: 0px;

  }
  .editorMedia {
    grid-area: media;
    height: 100%;
    border: solid 1px var(--light-grey);
    background-color: #233d4e;
    color: white;
    display: block;
    overflow: hidden;
    position: relative;
  }
}
</style>

<style>
:root {
  /** colors */
  --dark-grey: #3d3d3d;
  --grey: #5b5b5b;
  --light-grey: #878787;

  /** layers for modals */
  --modal-dropdown-z: 3200;
  --modal-z: 3150;
  --modal-backdrop-z: 3100;

  /** layer for notifications panel */
  --notifs-z: 3000;

  /** navigation bar layers */
  --navbar-dropdown-z: 2050;
  --navbar-z: 2000;

  --video-thumb-overlay-z: 50;

  /** player layers */
  --player-z: 50;
  --player-controls-z: 100;

  /** areas */
  --edit-area-z: 50;

  /** timeine layers */
  --timeline-popup-z: 300;
  --timeline-cursor-z: 120;
  --timeline-break-z: 20;

  /** left handle wrapper should to have more z-index to prevent ovelaying from right handle wrapper */
  --timeline-left-handle-z: 16;
  --timeline-right-handle-z: 15;
  --timeline-slider-z: 15;
  --timeline-faded-z: 20;
  --timeline-label-z: 10;
  --timeline-duration-z: 30;
  --timeline-button-z: 170;
}

#app {
  font-family: Poppins, Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
  background-color: var(--light-grey);
  height: 100vh;
}

@font-face {
  font-family: "Poppins";
  font-weight: lighter;
  src: url("../assets/Poppins Font/PoppinsLight-l4Zw.otf") format("opentype");
}
@font-face {
  font-family: "Poppins";
  font-weight: bold;
  src: url("../assets/Poppins Font/PoppinsSemibold-8l8n.otf") format("opentype");
}
@font-face {
  font-family: "Poppins";
  font-weight: 500;
  src: url("../assets/Poppins Font/PoppinsMedium-1JPv.otf") format("opentype");
}

.el-loading-spinner .el-loading-text {
  white-space: pre-wrap;
}

#nav {
  padding: 30px;
}

#nav a {
  font-weight: bold;
  color: #2c3e50;
}

#nav a.router-link-exact-active {
  color: #42b983;
}

/* Scrollbars */
/* width */
.scrollable ::-webkit-scrollbar {
  width: 15px;
}

/* .scroll-invisible::-webkit-scrollbar {
  display: none;
} */

.scroll-invisible {
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
}

/* Track */
.scrollable ::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

/* Handle */
.scrollable ::-webkit-scrollbar-thumb {
  background: var(--dark-grey);
  border-radius: 10px;
}

/* Handle on hover */
.scrollable ::-webkit-scrollbar-thumb:hover {
  background: #2d2d2d;
  border-radius: 10px;
}

/** modal */
.modal-select-dropdown {
  z-index: var(--modal-dropdown-z) !important;
}
</style>
